/**
 * Reusable image attach: thumbnails strip (ChatGPT-style) + trigger button.
 * Use AttachImageThumbnails above the textarea and AttachImageButton in the actions row next to voice.
 */

import React, { useRef, useCallback } from "react";

import { MAX_FILE_BYTES } from "./AttachFile";

import "./AttachImage.scss";

export const readFilesAsDataUrls = (
  files: FileList | null,
  onReject?: (filenames: string[]) => void,
): Promise<string[]> => {
  if (!files?.length) {
    return Promise.resolve([]);
  }
  const rejected: string[] = [];
  const toRead = Array.from(files).filter((f) => {
    if (!f.type.startsWith("image/")) {
      return false;
    }
    if (f.size > MAX_FILE_BYTES) {
      rejected.push(f.name);
      return false;
    }
    return true;
  });
  if (rejected.length && onReject) {
    onReject(rejected);
  }
  if (!toRead.length) {
    return Promise.resolve([]);
  }
  return new Promise((resolve) => {
    const results: string[] = new Array(toRead.length);
    let done = 0;
    toRead.forEach((f, i) => {
      const reader = new FileReader();
      reader.onload = () => {
        results[i] = reader.result as string;
        done += 1;
        if (done === toRead.length) {
          resolve(results);
        }
      };
      reader.readAsDataURL(f);
    });
  });
};

export interface AttachImageThumbnailsProps {
  urls: string[];
  onRemove: (index: number) => void;
  className?: string;
}

export const AttachImageThumbnails: React.FC<AttachImageThumbnailsProps> = ({
  urls,
  onRemove,
  className = "",
}) => {
  if (!urls.length) {
    return null;
  }
  return (
    <div className={`attach-image__thumbnails ${className}`.trim()}>
      {urls.map((url, i) => (
        <div key={i} className="attach-image__thumb">
          <img src={url} alt="" className="attach-image__thumb-img" />
          <button
            type="button"
            className="attach-image__thumb-remove"
            onClick={() => onRemove(i)}
            aria-label="Remove image"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      ))}
    </div>
  );
};

export interface AttachImageButtonProps {
  onAdd: (urls: string[]) => void;
  disabled?: boolean;
  /** Button class for alignment with voice btn */
  buttonClassName?: string;
  title?: string;
}

export const AttachImageButton: React.FC<AttachImageButtonProps> = ({
  onAdd,
  disabled = false,
  buttonClassName = "",
  title = "Add image",
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const urls = await readFilesAsDataUrls(e.target.files);
      if (urls.length) {
        onAdd(urls);
      }
      e.target.value = "";
    },
    [onAdd],
  );

  return (
    <>
      <button
        type="button"
        className={buttonClassName}
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        title={title}
        aria-label={title}
      >
        <span className="material-symbols-outlined">add_photo_alternate</span>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="attach-image__file-input"
        onChange={handleSelect}
      />
    </>
  );
};
